import { readDispatchDateFromSearch, readDispatchMonthFromSearch } from "./dispatchDateUrls.js";
import { buildDispatchDayControlsModel, resolveDispatchDayStatus } from "./dispatchDayControls.js";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeInlineTextList(values) {
  return Array.isArray(values)
    ? values.map((value) => normalizeInlineText(value)).filter(Boolean)
    : [];
}

function readSourceDataState(initialResultsData = null) {
  return initialResultsData?.source_data_state && typeof initialResultsData.source_data_state === "object"
    ? initialResultsData.source_data_state
    : null;
}

function buildLatestOptimizationState(initialResultsData = null) {
  const code = normalizeInlineText(initialResultsData?.code);
  const sourceDataState = readSourceDataState(initialResultsData);
  const sourceDataStatus = normalizeInlineText(sourceDataState?.status).toLowerCase();
  const sourceDataReasons = normalizeInlineTextList(sourceDataState?.reasons);
  const isSourceStale = sourceDataState?.stale === true
    || sourceDataStatus === "stale"
    || initialResultsData?.is_operationally_current === false;

  return {
    code,
    generatedAt: normalizeInlineText(initialResultsData?.generated_at),
    isSourceStale,
    resource: normalizeInlineText(initialResultsData?.resource),
    sourceDataDispatchDates: normalizeInlineTextList(sourceDataState?.dispatch_dates),
    sourceDataReason: normalizeInlineText(sourceDataState?.reason),
    sourceDataReasons,
    sourceDataStatus,
    sourceDataUpdatedAt: normalizeInlineText(sourceDataState?.updated_at),
  };
}

function buildLoadedResultsPageState(initialResultsData = null) {
  const latestOptimizationState = buildLatestOptimizationState(initialResultsData);
  const hasConsultableStalePayload = latestOptimizationState.isSourceStale === true;

  return {
    apiErrorMessage: hasConsultableStalePayload ? normalizeInlineText(initialResultsData?.error) : "",
    dispatchDayControls: null,
    dispatchDays: [],
    hasDispatchDays: false,
    isOperationallyCurrent: initialResultsData?.is_operationally_current !== false,
    kind: "loaded",
    missingLatestOptimization: null,
    selectedDispatchDate: "",
    selectedDispatchDay: null,
    selectedDispatchStatus: null,
    showArchive: false,
    staleLatestOptimization: hasConsultableStalePayload ? latestOptimizationState : null,
    suggestedDispatchDate: "",
  };
}

function readDispatchArchiveState(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  return params.get("dispatchArchive") === "1";
}

function resolveSelectedDispatchDay(dispatchDays = [], selectedDispatchDate = "") {
  const normalizedSelectedDispatchDate = normalizeInlineText(selectedDispatchDate);
  if (!normalizedSelectedDispatchDate) {
    return null;
  }

  return dispatchDays.find(
    (item) => normalizeInlineText(item?.dispatch_date) === normalizedSelectedDispatchDate,
  ) || null;
}

function resolveSuggestedDispatchDate(dispatchDays = [], defaultDispatchDate = "", payloadDispatchDate = "") {
  const normalizedDefaultDispatchDate = normalizeInlineText(defaultDispatchDate);
  if (normalizedDefaultDispatchDate) {
    return normalizedDefaultDispatchDate;
  }

  const normalizedPayloadDispatchDate = normalizeInlineText(payloadDispatchDate);
  if (normalizedPayloadDispatchDate) {
    return normalizedPayloadDispatchDate;
  }

  const firstOpenDay = dispatchDays.find((item) => item?.is_closed !== true);
  return normalizeInlineText(firstOpenDay?.dispatch_date) || normalizeInlineText(dispatchDays[0]?.dispatch_date);
}

export function isMissingLatestOptimizationSnapshot(initialResultsData) {
  return Boolean(
    initialResultsData
      && typeof initialResultsData === "object"
      && !Array.isArray(initialResultsData)
      && initialResultsData.code === "resource_not_found"
      && initialResultsData.resource === "latest_optimization",
  );
}

export function resolveResultsPageState({ initialResultsData = null, locationSearch = "" } = {}) {
  if (!isMissingLatestOptimizationSnapshot(initialResultsData)) {
    return buildLoadedResultsPageState(initialResultsData);
  }

  const dispatchDays = Array.isArray(initialResultsData?.dispatch_days)
    ? initialResultsData.dispatch_days.filter(Boolean)
    : [];
  const showArchive = readDispatchArchiveState(locationSearch);
  const selectedDispatchDate = readDispatchDateFromSearch(locationSearch);
  const selectedDispatchDay = resolveSelectedDispatchDay(dispatchDays, selectedDispatchDate);
  const hasValidSelection = Boolean(selectedDispatchDate && selectedDispatchDay);
  const suggestedDispatchDate = resolveSuggestedDispatchDate(
    dispatchDays,
    initialResultsData?.default_dispatch_date,
    initialResultsData?.dispatch_date,
  );
  const dispatchDayControls = buildDispatchDayControlsModel({
    activeDispatchDate: hasValidSelection ? selectedDispatchDate : "",
    activeDispatchMonth: readDispatchMonthFromSearch(locationSearch),
    activeDispatchState: hasValidSelection ? selectedDispatchDay?.dispatch_day_state || "" : "",
    dispatchDays,
    planningSummary: initialResultsData?.planning_summary,
    selectedLabel: hasValidSelection
      ? normalizeInlineText(selectedDispatchDay?.label) || selectedDispatchDate
      : "Choisissez une journée",
    showArchive,
  });
  const selectedDispatchStatus = hasValidSelection
    ? (
      dispatchDayControls?.activeItem?.status
      || dispatchDayControls?.activeStatus
      || resolveDispatchDayStatus(selectedDispatchDay)
    )
    : null;

  return {
    apiErrorMessage: normalizeInlineText(initialResultsData?.error),
    dispatchDayControls,
    dispatchDays,
    hasDispatchDays: dispatchDays.length > 0,
    isOperationallyCurrent: initialResultsData?.is_operationally_current !== false,
    kind: hasValidSelection ? "selected-day-no-snapshot" : "choose-day",
    missingLatestOptimization: buildLatestOptimizationState(initialResultsData),
    selectedDispatchDate,
    selectedDispatchDay,
    selectedDispatchStatus,
    showArchive,
    staleLatestOptimization: null,
    suggestedDispatchDate,
  };
}